import React from "react";
import {
  FiArrowRight,
  FiCalendar,
  FiExternalLink,
  FiMapPin,
} from "react-icons/fi";

import Reveal from "../../../components/ui/Reveal";

import {
  cn,
  FALLBACK_EVENT_IMAGE,
  getEventStatusLabel,
  isSameDay,
  parseDate,
} from "../utils/eventsUtils";

function EventDateBadge({ event }) {
  const startDate = parseDate(event.startDate);
  const endDate = parseDate(event.endDate);

  const dateParts = event.dateParts;
  const endDateParts = event.endDateParts;

  const hasEndDate = startDate && endDate && !isSameDay(startDate, endDate);
  const differentYear =
    startDate && endDate && startDate.getFullYear() !== endDate.getFullYear();

  if (!dateParts) return null;

  return (
    <div className="absolute left-4 top-4 z-10 min-w-[74px] overflow-hidden rounded-2xl bg-white/95 text-center shadow-lg backdrop-blur dark:bg-slate-950/95">
      <div className="bg-[#1277B9] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
        {dateParts.month}
      </div>

      <div className="px-3 py-2">
        <p className="text-2xl font-bold leading-none text-slate-950 dark:text-white">
          {dateParts.day}
        </p>

        <p className="mt-1 text-[11px] font-bold text-slate-400">
          {event.allDay ? dateParts.year : dateParts.time || dateParts.year}
        </p>
      </div>

      {hasEndDate && endDateParts && (
        <div className="border-t border-slate-200 bg-slate-50 px-2.5 py-1.5 dark:border-slate-700 dark:bg-slate-900">
          <p className="text-[7px] font-semibold uppercase tracking-[0.16em] text-slate-900">
            jusqu’au
          </p>

          <p className="mt-0.5 text-[11px] font-bold uppercase leading-none text-[#1277B9] dark:text-sky-300">
            {endDateParts.day} {endDateParts.month}
          </p>

          {differentYear && (
            <p className="mt-0.5 text-[10px] font-bold text-slate-400">
              {endDateParts.year}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function EventStatusPill({ status }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold",
        status === "past"
          ? "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
          : status === "ongoing"
            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
            : "bg-[#1277B9]/10 text-[#1277B9] dark:bg-[#1277B9]/20 dark:text-sky-300",
      )}
    >
      {getEventStatusLabel(status)}
    </span>
  );
}

export default function EventCard({ event, onOpen, delay = 0 }) {
  const handleOpen = () => {
    onOpen?.(event);
  };

  return (
    <Reveal delay={delay}>
      <article className="group flex h-full flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[#1277B9]/50 hover:shadow-xl dark:border-slate-800 dark:bg-slate-950">
        <div className="relative overflow-hidden bg-slate-100 dark:bg-slate-900">
          <img
            src={event.image}
            alt={event.title}
            loading="lazy"
            className="aspect-[16/10] w-full object-cover transition duration-500 group-hover:scale-105"
            onError={(e) => {
              e.currentTarget.src = FALLBACK_EVENT_IMAGE;
            }}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-slate-950/10 to-transparent" />

          <EventDateBadge event={event} />

          <div className="absolute bottom-4 left-4 right-4 flex flex-wrap items-center gap-2">
            {event.category && (
              <span className="inline-flex rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-[#1277B9] shadow-sm backdrop-blur dark:bg-slate-950/90 dark:text-sky-300">
                {event.category}
              </span>
            )}

            <span className="inline-flex rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-slate-700 shadow-sm backdrop-blur dark:bg-slate-950/90 dark:text-slate-200">
              {event.allDay ? "Journée complète" : event.dateParts?.time}
            </span>
          </div>
        </div>

        <div className="flex flex-1 flex-col p-5">
          <div className="flex flex-wrap items-center gap-2">
            <EventStatusPill status={event.status} />

            {!event.allDay && event.dateParts?.time && (
              <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                {event.dateParts.time}
              </span>
            )}

            {event.externalLink && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-bold text-violet-700 dark:border-violet-500/20 dark:bg-violet-500/10 dark:text-violet-300">
                <FiExternalLink className="h-3.5 w-3.5" />
                Lien
              </span>
            )}
          </div>

          <h2 className="mt-3 line-clamp-2 text-xl font-bold leading-snug text-slate-950 dark:text-white">
            {event.title || "Sans titre"}
          </h2>

          {event.subtitle && (
            <p className="mt-1 line-clamp-1 text-sm font-bold text-[#1277B9] dark:text-sky-300">
              {event.subtitle}
            </p>
          )}

          {event.periodLabel && (
            <div className="mt-4 flex items-start gap-2 text-sm font-bold text-slate-500 dark:text-slate-400">
              <FiCalendar className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="line-clamp-2">{event.periodLabel}</span>
            </div>
          )}

          {event.location && (
            <div className="mt-2 flex items-start gap-2 text-sm font-semibold text-slate-500 dark:text-slate-400">
              <FiMapPin className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="line-clamp-2">{event.location}</span>
            </div>
          )}

          <p className="mt-4 line-clamp-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
            {event.descriptionPreview || "Aucune description renseignée."}
          </p>

          <div className="mt-auto flex flex-wrap gap-2 pt-5">
            <button
              type="button"
              onClick={handleOpen}
              className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl bg-[#1277B9] px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-[#0f659d] focus:outline-none focus:ring-2 focus:ring-[#1277B9]/40 sm:flex-none"
            >
              Voir l’événement
              <FiArrowRight className="h-4 w-4" />
            </button>

            {event.externalLink && (
              <a
                href={event.externalLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:border-[#1277B9]/50 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800 sm:flex-none"
              >
                Site externe
                <FiExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>
      </article>
    </Reveal>
  );
}
